import { FC, Fragment, JSX, useMemo, useState } from 'react';
import { DataTable, PageBreadcrumbs } from '@consolidocs-ui/components';
import { useNavigate } from 'react-router';
import SearchIcon from '@mui/icons-material/Search';
import { Button, FormControl, FormLabel, Input, Sheet } from '@mui/joy';
import { useGetContractsQuery } from '@consolidocs-ui/data-access/ContractApi';
import { RowMenu } from './RowMenu';
import { ContractType } from '@consolidocs-ui/types';

export const Contracts: FC = (): JSX.Element => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const { data: contractsList, isLoading } = useGetContractsQuery();

  const contracts = useMemo(() => contractsList?.data || [], [contractsList]);

  const totalPages = Math.max(1, Math.ceil(contracts.length / 10));
  const filteredContracts = useMemo(() => {
    const lowerSearch = searchQuery.trim().toLowerCase();
    return contracts
      .filter((contract: ContractType) => {
        return (
          contract?.name?.toLowerCase().includes(lowerSearch) ||
          contract?.code?.toLowerCase().includes(lowerSearch) ||
          contract?.description?.toLowerCase().includes(lowerSearch)
        );
      })
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
  }, [contracts, searchQuery]);

  const paginatedContracts = useMemo(() => {
    const start = (page - 1) * 10;
    return filteredContracts.slice(start, start + 10);
  }, [page, filteredContracts]);

  return (
    <Fragment>
      <Sheet
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          backgroundColor: '#F0F4F8',
          mb: 1,
        }}
      >
        <PageBreadcrumbs
          items={[
            {
              label: 'All Contracts',
            },
          ]}
        />
        <Button onClick={() => navigate('/contracts/create')}>
          Create Contract
        </Button>
      </Sheet>

      <FormControl sx={{ flex: 1, mb: 2 }} size="sm">
        <FormLabel>Search for Contracts</FormLabel>
        <Input
          size="sm"
          placeholder="Search"
          startDecorator={<SearchIcon />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </FormControl>

      <DataTable<ContractType>
        data={paginatedContracts}
        isLoading={isLoading}
        page={page}
        setPage={setPage}
        totalPages={totalPages}
        selectedKeys={selected}
        onSelectChange={setSelected}
        getRowKey={(item) => item?.id || ''}
        onRowClick={(item) => {
          if (!item?.id) return;
          navigate(`/contracts/edit/${item?.id}`);
        }}
        columns={[
          { key: '__select__', align: 'center' },
          { key: 'id', label: 'ID', align: 'left' },
          { key: 'name', label: 'Name', align: 'left' },
          { key: 'code', label: 'Code', align: 'center' },
          { key: 'description', label: 'Description', align: 'center' },
          { key: 'industry_types', label: 'Industry Types', align: 'center' },
          { key: 'actions', label: 'Actions', align: 'center' },
        ]}
        children={{
          actions: ({ item }) => (
            <RowMenu
              contractId={item?.id || ''}
              onClick={(e) => e.stopPropagation()}
            />
          ),
          industry_types: ({ item }) =>
            item?.industry_types
              ?.map((type) => (typeof type === 'string' ? type : type.name))
              .join(', ') || 'N/A',
        }}
      />
    </Fragment>
  );
};
